/**
 * Extract a human-readable error message from various error formats
 */
export const extractErrorMessage = (error: unknown): string => {
  // Handle null/undefined
  if (!error) {
    return 'An unexpected error occurred';
  }

  // If it's already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // Type guard for objects
  if (typeof error !== 'object') {
    return 'An unexpected error occurred';
  }

  const err = error as Record<string, unknown>;

  // Handle Axios error format
  if (err.response && typeof err.response === 'object') {
    const response = err.response as Record<string, unknown>;
    const data = response.data;
    
    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      
      // FastAPI validation error format: { detail: [...] }
      if (Array.isArray(dataObj.detail)) {
        // Extract first error message from validation errors
        const firstError = dataObj.detail[0];
        if (firstError && typeof firstError === 'object') {
          const errorObj = firstError as Record<string, unknown>;
          if (typeof errorObj.msg === 'string') {
            return errorObj.msg;
          }
        }
        return 'Validation error occurred';
      }
      
      // FastAPI single error format: { detail: "message" }
      if (typeof dataObj.detail === 'string') {
        return dataObj.detail;
      }
      
      // Generic error message field
      if (typeof dataObj.message === 'string') {
        return dataObj.message;
      }
      
      // Generic error field
      if (typeof dataObj.error === 'string') {
        return dataObj.error;
      }
    }
  }

  // Handle Error objects
  if (err.message && typeof err.message === 'string') {
    return err.message;
  }

  // Handle objects with msg property (FastAPI validation error item)
  if (err.msg && typeof err.msg === 'string') {
    return err.msg;
  }

  // Fallback
  return 'An unexpected error occurred';
};

/**
 * Extract all error messages from FastAPI validation errors
 */
export const extractAllErrorMessages = (error: unknown): string[] => {
  if (!error || typeof error !== 'object') {
    return [extractErrorMessage(error)];
  }

  const err = error as Record<string, unknown>;
  const response = err.response as Record<string, unknown> | undefined;
  const data = response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;

  if (!Array.isArray(detail)) {
    return [extractErrorMessage(error)];
  }

  return detail
    .map((item: unknown) => {
      if (item && typeof item === 'object') {
        const errorObj = item as Record<string, unknown>;
        return typeof errorObj.msg === 'string' ? errorObj.msg : 'Validation error';
      }
      return 'Validation error';
    })
    .filter(Boolean);
};
